import React, { FC, useState } from 'react';
import { useParams } from 'react-router';
import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildClientEntity } from 'utils/helpers/client';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import { Column } from 'react-table';
import { formatDate } from 'utils/helpers/formatters';
import sanitizeHTML from 'utils/helpers/editor';
import Button from 'components/Button';
import API from 'core/Api';
import TooltipWrapper from 'components/TooltipWrapper';
import {
  FaFileAlt,
  FaFileUpload,
  FaPrint,
  FaPlus,
  FaPen,
  FaEdit,
  FaCheck,
  FaArchive,
  FaTimesCircle,
  FaInbox,
  FaChevronLeft,
  FaTrash,
  FaPause,
} from 'react-icons/fa';
import { IoSendSharp } from 'react-icons/io5';
import styled from 'styled-components';
import Spinner from 'components/Spinner';
import { IDocumentActivity } from './types';

const Screen: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { client } = useActiveClientContext();
  const history = useHistory();
  const location = useLocation<{ fromReports?: boolean }>();
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const [isLoading, setIsLoading] = useState(false);

  const renderIcon = (value: string) => {
    if (value === 'create') return <FaPlus size={16} />;
    if (value === 'approve') return <FaCheck size={16} />;
    if (value === 'download') return <FaPrint size={16} />;
    if (value === 'sign') return <FaPen size={16} />;
    if (value === 'edit') return <FaEdit size={16} />;
    if (value === 'send-to-employee' || value === 'send-to-employer') return <IoSendSharp size={16} />;
    if (value === 'receive-by-employee' || value === 'receive-by-employer') return <FaInbox size={16} />;
    if (value === 'archive') return <FaArchive size={16} />;
    if (value === 'reject') return <FaTimesCircle size={16} />;
    if (value === 'moved-to-bin') return <FaTrash size={16} />;
    if (value === 'interrupt-leave') return <FaPause size={16} />;
    return null;
  };

  const columns: Array<Column<IDocumentActivity>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('documents.activity.action') || '',
      accessor: 'action',
      width: '5%',
      disableSortBy: true,
      Cell: ({ value }) => {
        return <div className="text-center">{renderIcon(value)}</div>;
      },
    },
    {
      Header: t('documents.activity.description') || '',
      accessor: 'action_description',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const { action_description_en: actionDescriptionEn } = row.original;
        const desc = language === 'en' && actionDescriptionEn ? actionDescriptionEn : value;
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(desc) }} />;
      },
    },
    {
      Header: t('documents.activity.date') || '',
      accessor: 'created_at',
      width: '20%',
      disableSortBy: true,
      Cell: ({ value }) => {
        const formattedDate = formatDate(new Date(value), 'dd.mm.Y hh:ii:ss');
        return <>{formattedDate}</>;
      },
    },
    {
      Header: () => <div className="text-right">{t('documents.activity.receipt')}</div>,
      accessor: 'receipt',
      width: '5%',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="text-right">
          <TooltipWrapper
            id="tooltip-print"
            overlay={t('documents.table.preview')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              outlined
              disabled={isLoading}
              onClick={() => {
                setIsLoading(true);
                API.get(`document-logs/${row.original.id}/download`)
                  .then(res => {
                    setTimeout(() => {
                      window.open(res.data, '_blank');
                    });
                  })
                  .finally(() => setIsLoading(false));
              }}
            >
              {!isLoading && <FaPrint size={14} />}

              {isLoading && <Spinner size={14} />}
            </Button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];
  const renderDocIcon = (type: string) => {
    if (type === 'internal')
      return (
        <TooltipWrapper
          id="tooltip-internalDoc"
          overlay={t('documents.internalDoc')}
        >
          <FaFileAlt
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );
    if (type === 'external')
      return (
        <TooltipWrapper
          id="tooltip-externalDoc"
          overlay={t('documents.externalDoc')}
        >
          <FaFileUpload
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );

    return null;
  };

  return (
    <MainLayout title={`${t('documents.docActivity')} ${formatDate(new Date(), 'dd.mm.Y hh:ii:ss')}`}>
      <FetchTable
        entityName={buildClientEntity('document-logs', client)}
        entityEndpoint="document-logs"
        columns={columns}
        searchParams={{
          document_id: id,
        }}
        hideFilterBar
        hasTitle
        pagination
        pageLimit={20}
        updateTitle={data => {
          const document = data?.items.length ? data?.items[0].document : {};
          const { name, name_en: nameEn, type, number } = document;
          const buildName = nameEn ? `${name} / ${nameEn}` : name;
          return (
            <TitleWrapper>
              <div>
                {renderDocIcon(type)}
                <span className="ml-2">
                  {buildName || 'Документ'} {t('common.with')} &#8470;&nbsp;
                  {number}
                </span>
              </div>
              <Button
                type="button"
                btnType="secondary"
                outlined
                className="ml-4"
                onClick={() => history.goBack()}
              >
                <FaChevronLeft
                  size={12}
                  style={{ marginRight: '0.5rem' }}
                />
                {location?.state?.fromReports
                  ? t('documents.activity.backToReports')
                  : t('documents.activity.backToDocsList')}
              </Button>
            </TitleWrapper>
          );
        }}
      />
    </MainLayout>
  );
};

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export default Screen;
